import { useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import {
  Download,
  ExternalLink,
  Mail,
  MoreHorizontal,
  PackageCheck,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { PageHeader } from "@/components/shared/page-header";
import { StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input, NativeSelect } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatMoney } from "@/features/clients/constants";
import {
  useDeletePurchaseOrder,
  usePurchaseOrders,
  useUpdatePurchaseOrderStatus,
} from "@/features/purchase/api";
import { PO_STATUSES, canReceive, labelFor, statusTone } from "@/features/purchase/constants";
import type { PurchaseOrderListRow, PurchaseOrderStatus } from "@/features/purchase/types";
import { useVendors } from "@/features/vendors/api";
import { isSupabaseConfigured } from "@/lib/supabase";
import { cn } from "@/lib/utils";

export function PurchaseOrdersPage() {
  return (
    <ErrorBoundary title="Purchase orders crashed">
      <PurchaseOrdersInner />
    </ErrorBoundary>
  );
}

function PurchaseOrdersInner() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { vendor?: string };
  const vendorFilter = search.vendor ?? "all";

  const { data = [], isLoading, error, refetch, isFetching } = usePurchaseOrders(
    vendorFilter !== "all" ? { vendorId: vendorFilter } : undefined,
  );
  const { data: vendors = [] } = useVendors();
  const remove = useDeletePurchaseOrder();
  const updateStatus = useUpdatePurchaseOrderStatus();

  const [statusFilter, setStatusFilter] = useState<"all" | PurchaseOrderStatus>("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");
  const [deleting, setDeleting] = useState<PurchaseOrderListRow | null>(null);

  const vendorById = useMemo(() => new Map(vendors.map((v) => [v.id, v])), [vendors]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return data.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (from && row.issue_date < from) return false;
      if (to && row.issue_date > to) return false;
      if (!query) return true;
      return [row.po_number, row.vendor_name, row.vendor_code]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [data, statusFilter, from, to, q]);

  const kpis = useMemo(() => {
    const open = data.filter((r) =>
      ["draft", "sent", "confirmed", "partial"].includes(r.status),
    );
    return {
      draft: data.filter((r) => r.status === "draft").length,
      openValue: open.reduce((s, r) => s + r.grand_total, 0),
      partial: data.filter((r) => r.status === "partial").length,
      confirmed: data.filter((r) => r.status === "confirmed").length,
    };
  }, [data]);

  function openPo(row: PurchaseOrderListRow) {
    void navigate({ to: "/purchase/orders/$poId", params: { poId: row.id } });
  }

  function receivePo(row: PurchaseOrderListRow) {
    void navigate({ to: "/purchase/orders/$poId/receive", params: { poId: row.id } });
  }

  async function emailPo(row: PurchaseOrderListRow) {
    const vendor = vendorById.get(row.vendor_id);
    const email =
      vendor?.email?.trim() ||
      vendor?.primary_contact?.email?.trim() ||
      "";
    if (!email) {
      toast.error("No email on this vendor — add one under Vendors");
      return;
    }
    const subject = encodeURIComponent(`Purchase Order ${row.po_number}`);
    const body = encodeURIComponent(
      [
        `Dear ${vendor?.name ?? "Vendor"},`,
        "",
        `Please find purchase order ${row.po_number} dated ${format(parseISO(row.issue_date), "dd MMM yyyy")}.`,
        `Order value: ${formatMoney(row.grand_total)}.`,
        "",
        "Kindly confirm receipt of this order.",
        "",
        "— Factory Sarathi",
      ].join("\n"),
    );
    const mailto = `mailto:${email}?subject=${subject}&body=${body}`;
    // Prefer opening mail without navigating away from the SPA (which clears forms)
    const opened = window.open(mailto, "_blank");
    if (!opened) {
      const a = document.createElement("a");
      a.href = mailto;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    if (row.status === "draft") {
      try {
        await updateStatus.mutateAsync({ id: row.id, status: "sent" });
      } catch {
        /* toast from mutation */
      }
    } else {
      toast.success(`Email draft opened for ${row.po_number}`);
    }
  }

  function exportCsv() {
    const header = ["po_number", "vendor", "issue_date", "status", "grand_total", "lines"];
    const body = filtered.map((r) =>
      [
        r.po_number,
        csv(r.vendor_name ?? ""),
        r.issue_date,
        r.status,
        r.grand_total,
        r.line_count ?? 0,
      ].join(","),
    );
    const blob = new Blob([[header.join(","), ...body].join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `purchase-orders-${format(new Date(), "yyyyMMdd-HHmm")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!isSupabaseConfigured) {
    return (
      <EmptyState
        title="Supabase not configured"
        description="Configure Supabase to manage purchase orders."
      />
    );
  }

  return (
    <div className="space-y-3">
      <PageHeader
        title="Purchase orders"
        description="Order coils and materials from vendors — GST applied from company vs vendor place of supply."
        actions={
          <div className="flex items-center gap-1.5">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => void refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={cn(isFetching && "animate-spin")} />
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={exportCsv} disabled={!filtered.length}>
              <Download />
              Export
            </Button>
            <Button asChild size="sm">
              <Link to="/purchase/orders/new">
                <Plus />
                New PO
              </Link>
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <Kpi label="Open value" value={formatMoney(kpis.openValue)} />
        <Kpi label="Drafts" value={String(kpis.draft)} />
        <Kpi label="Confirmed" value={String(kpis.confirmed)} />
        <Kpi label="Partial" value={String(kpis.partial)} />
      </div>

      <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-card p-3">
        <Filter label="Vendor">
          <NativeSelect
            value={vendorFilter}
            className="h-8 w-52"
            onChange={(e) => {
              const v = e.target.value;
              void navigate({
                to: "/purchase",
                search: v === "all" ? {} : { vendor: v },
              });
            }}
          >
            <option value="all">All vendors</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.code} · {v.name}
              </option>
            ))}
          </NativeSelect>
        </Filter>
        <Filter label="Status">
          <NativeSelect
            value={statusFilter}
            className="h-8 w-36"
            onChange={(e) => setStatusFilter(e.target.value as "all" | PurchaseOrderStatus)}
          >
            <option value="all">All</option>
            {PO_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </NativeSelect>
        </Filter>
        <Filter label="From">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 w-36" />
        </Filter>
        <Filter label="To">
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 w-36" />
        </Filter>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            className="h-8 w-44 pl-7"
          />
        </div>
      </div>

      {error ? (
        <p className="text-sm text-destructive">{(error as Error).message}</p>
      ) : isLoading ? (
        <p className="py-12 text-center text-xs text-muted-foreground">Loading…</p>
      ) : data.length === 0 ? (
        <EmptyState
          title="No purchase orders yet"
          description="Create a PO against a vendor with material lines and GST."
          action={
            <Button asChild size="sm">
              <Link to="/purchase/orders/new">
                <Plus />
                New PO
              </Link>
            </Button>
          }
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>PO</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="w-28 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={6} className="py-10 text-center text-xs text-muted-foreground">
                    No POs match the filters.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((row) => (
                  <TableRow
                    key={row.id}
                    className="group cursor-pointer"
                    onClick={() => openPo(row)}
                  >
                    <TableCell>
                      <div className="font-mono text-xs font-medium">{row.po_number}</div>
                      <div className="text-2xs text-muted-foreground">{row.line_count ?? 0} lines</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-xs font-medium">{row.vendor_name}</div>
                      <div className="font-mono text-2xs text-muted-foreground">{row.vendor_code}</div>
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {format(parseISO(row.issue_date), "dd MMM yyyy")}
                    </TableCell>
                    <TableCell>
                      <StatusDot tone={statusTone(row.status)}>
                        {labelFor(PO_STATUSES, row.status)}
                      </StatusDot>
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {formatMoney(row.grand_total)}
                    </TableCell>
                    <TableCell
                      className="text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="inline-flex items-center justify-end gap-0.5">
                        {canReceive(row.status) ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            title="Receive items"
                            onClick={() => receivePo(row)}
                          >
                            <PackageCheck className="size-3.5" />
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          title="Email / resend"
                          onClick={() => void emailPo(row)}
                        >
                          <Mail className="size-3.5" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button type="button" variant="ghost" size="icon-sm" title="More actions">
                              <MoreHorizontal className="size-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuItem onSelect={() => openPo(row)}>
                              <ExternalLink className="size-3.5" />
                              Open PO
                            </DropdownMenuItem>
                            {canReceive(row.status) ? (
                              <DropdownMenuItem onSelect={() => receivePo(row)}>
                                <PackageCheck className="size-3.5" />
                                Receive items
                              </DropdownMenuItem>
                            ) : null}
                            <DropdownMenuItem onSelect={() => void emailPo(row)}>
                              <Mail className="size-3.5" />
                              {row.status === "draft" ? "Send via email" : "Resend via email"}
                            </DropdownMenuItem>
                            {row.status === "draft" ? (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onSelect={() => setDeleting(row)}
                                >
                                  <Trash2 className="size-3.5" />
                                  Delete draft
                                </DropdownMenuItem>
                              </>
                            ) : null}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(v) => !v && setDeleting(null)}
        title="Delete draft PO?"
        description={deleting ? `${deleting.po_number} will be permanently removed.` : undefined}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2">
      <div className="text-2xs text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function Filter({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <span className="text-2xs text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

function csv(value: string) {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}
